import React from "react";
import { Helmet } from 'react-helmet';
import "./PrivacyPolicy.css";
import { useNavigate } from "react-router-dom";


function PrivacyPolicy() {
  const navigate = useNavigate();


  return (
    <div className="privacy-policy-container">
      <Helmet>
        <title>Privacy Policy | VaahanBuddy - Vehicle Services in India</title>
        <meta name="description" content="Learn how VaahanBuddy protects your personal data, including phone numbers, vehicle details, and user rights, in compliance with India's Digital Personal Data Protection Act, 2023." />
        <meta name="keywords" content="VaahanBuddy privacy policy, vehicle services, data protection, India, Digital Personal Data Protection Act, user rights, RTO services" />
        <meta property="og:title" content="Privacy Policy | VaahanBuddy" />
        <meta property="og:description" content="Understand VaahanBuddy's commitment to safeguarding your personal data and user rights for vehicle services in India." />
        <meta property="og:image" content="https://rtoxpert.com/assets/logo.png" />
        <meta property="og:url" content="https://rtoxpert.com/privacy-policy" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="privacy-policy-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Privacy Policy</h1>
      </div>
      <div className="privacy-policy-content">
        <section>
          <h2>Introduction</h2>
          <p><strong>Effective Date: June 27, 2025</strong></p>
          <p>
            Welcome to the Privacy Policy of VaahanBuddy - Vehicle Services Assistant for India. VaahanBuddy is a private platform that helps users with vehicle-related tasks in India via independent agents. This document outlines how we collect, use, disclose, and protect your personal information when you use our platform and services.
          </p>
          <p>
            At VaahanBuddy, we are committed to safeguarding your privacy. We respect your personal data and ensure it is handled with care in compliance with applicable laws, including the Digital Personal Data Protection Act, 2023 (India). Please read this Privacy Policy carefully to understand our practices.
          </p>
          <div className="important-disclaimer">
            <h3>🔒 Important Disclaimer:</h3>
            <p>
              <strong>VaahanBuddy is NOT affiliated with any government entity, Regional Transport Office (RTO), or government authority.</strong> All services are independently provided by third-party agents who follow standard public procedures. We do not represent any government body and do not have any government authorization.
            </p>
          </div>
        </section>


        <section>
          <h2>Information We Collect</h2>
          <p>We collect information to provide our vehicle services through the following methods:</p>
          
          <h3>Required Information</h3>
          <ul>
            <li><strong>Phone Number:</strong> Required for OTP-based login and account creation. This is the only mandatory information needed to use our platform.</li>
          </ul>


          <h3>Optional Information (Provided Voluntarily)</h3>
          <ul>
            <li><strong>Personal Details:</strong> Name, email address, profile picture, and address – all optional for an enhanced service experience.</li>
            <li><strong>Vehicle Information:</strong> Registration number, make, model, year, and other vehicle details for service requests.</li>
            <li><strong>Document Storage:</strong> Vehicle-related documents (e.g., RC details) uploaded to your Digital Garage, stored securely in our private database.</li>
            <li><strong>Service Requests:</strong> Details you provide when requesting RTO services like NOC, ownership transfer, address change, etc.</li>
            <li><strong>Location Data:</strong> Accessed only when you use the "Nearby Services" feature (e.g., to show police stations or mechanics via Google Maps API). Location data is not stored permanently.</li>
            <li><strong>Communication Records:</strong> Records of interactions with our independent agents for service assistance.</li>
          </ul>


          <h3>Third-Party Data Sources</h3>
          <ul>
            <li><strong>RC & Challan Information:</strong> Fetched exclusively via <a href="https://gridlines.io/products/rc-api">Gridline.io</a> (API calls: <a href="https://api.gridlines.io/rc-api/fetch-detailed">RC API</a>, <a href="https://api.gridlines.io/rc-api/echallan/fetch">Challan API</a>), a licensed third-party provider accessing publicly available government RTO records. VaahanBuddy does not modify this data and is not liable for any inaccuracies in government databases.</li>
          </ul>


          <p>
            <strong>Important:</strong> All information except your phone number is provided voluntarily by users for a smoother experience and is not compulsory.
          </p>
        </section>


        <section>
          <h2>How Our Platform Works</h2>
          <p>Understanding our process helps explain how we use your information:</p>
          <ol>
            <li><strong>Account Creation:</strong> Register using only your phone number with OTP verification.</li>
            <li><strong>Service Request:</strong> Users fill in vehicle or service request details.</li>
            <li><strong>Agent Contact:</strong> Our independent agents reach out via phone or email to assist.</li>
            <li><strong>Standard Procedures:</strong> Agents follow standard public procedures (e.g., forms, documentation) – no government authorization is used or required.</li>
            <li><strong>Completion:</strong> Service is completed through standard channels with agent guidance.</li>
          </ol>
        </section>


        <section>
          <h2>How We Use Your Information</h2>
          <p>We use your information to deliver and enhance our services, including:</p>
          <ul>
            <li><strong>Account Management:</strong> Using your phone number for OTP-based login and account access.</li>
            <li><strong>Digital Garage:</strong> Securely storing your vehicle documents in our private database (users can delete documents anytime).</li>
            <li><strong>RTO Service Assistance:</strong> Our independent agents use your provided contact and vehicle details to guide you through standard procedures for NOC, ownership transfer, address changes, etc.</li>
            <li><strong>Vehicle Information Display:</strong> Showing RC and challan details fetched from Gridline.io's public RTO data access.</li>
            <li><strong>Vehicle Buy/Sell:</strong> Redirecting to certified platforms like Spinny and Cars24.</li>
            <li><strong>Accessories:</strong> Providing external links to platforms like Flipkart for vehicle accessories.</li>
            <li><strong>Nearby Services:</strong> Using Google Maps API to show nearby police stations or mechanics (location accessed only when feature is used).</li>
            <li><strong>DL Test Preparation:</strong> Providing practice questions and preparation materials for driving license tests.</li>
            <li><strong>Customer Support:</strong> Responding to inquiries via our contact channels.</li>
          </ul>
        </section>


        <section>
          <h2>Technical Infrastructure & Data Security</h2>
          <p>
            <strong>API Infrastructure:</strong> All service-related APIs are developed in-house and hosted securely on AWS EC2 instances. No third-party has access to this infrastructure. These APIs handle user registration, document upload, service request handling.
          </p>
          <p>
            <strong>Data Storage:</strong> Your data is stored securely in our private database, which is not publicly accessible. Only you can access your Digital Garage data (e.g., vehicle documents); our agents cannot access this data. We use industry-standard encryption and security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction.
          </p>
          <p>
            <strong>Data Retention:</strong> Data is retained only as long as needed to provide services. Upon your request to delete data or your account, it is removed immediately from our database, subject to legal requirements.
          </p>
          <p>
            <strong>User Control:</strong> You can delete any document or your entire account at any time directly from the app. You have complete control over your stored information.
          </p>
          <p>
            Despite our efforts, no online platform can guarantee absolute security. We encourage you to use strong passwords and avoid sharing sensitive information unnecessarily.
          </p>
        </section>


        <section>
          <h2>How We Share Your Information</h2>
          <p>We may share your information in the following cases:</p>
          <ul>
            <li><strong>With Our Independent Agents:</strong> Limited data (e.g., contact details or service request information you provide) is shared with authorized VaahanBuddy agents to assist with your service requests. Agents are bound by strict confidentiality obligations and use only standard public procedures.</li>
            <li><strong>With Service Providers:</strong> We engage trusted third-party providers (e.g., Gridline.io for RTO data, Google Maps for location services) who are contractually obligated to protect your data.</li>
            <li><strong>External Platform Redirects:</strong> No user data is shared when redirecting to vehicle buying/selling platforms (Spinny, Cars24) or accessory shopping (Flipkart). You interact directly with these platforms under their respective privacy policies.</li>
          </ul>
          <p>
            <strong>We do not sell your personal information to third parties for marketing purposes.</strong>
          </p>
        </section>


        <section>
          <h2>Third-Party Services & Data Sources</h2>
          <p>
            <strong>Gridline.io:</strong> We source vehicle and challan information through <a href="https://gridlines.io/products/rc-api">Gridline.io</a>, a licensed third-party service accessing publicly available government RTO data. While we strive for accuracy, we are not liable for discrepancies or errors in the data provided by Gridline.io or government RTO services. Please verify critical information directly with the relevant RTO office.
          </p>
          <p>
            <strong>External Platforms:</strong> VaahanBuddy may redirect to third-party services like Spinny, Cars24, or Flipkart. These platforms have their own privacy policies, and we are not responsible for their practices or content.
          </p>
          <p>
            <strong>Google Maps API:</strong> Used only for the "Nearby Services" feature when you specifically access it. Location data is not stored permanently.
          </p>
        </section>


        <section>
          <h2>Your Rights & Data Control</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data we hold.</li>
            <li><strong>Delete:</strong> Remove any document or your entire account directly from the app at any time.</li>
            <li><strong>Correct:</strong> Update inaccurate or incomplete data through the app.</li>
            <li><strong>Control:</strong> Choose what optional information to provide beyond your phone number.</li>
            <li><strong>Restrict:</strong> Object to or limit the processing of your data in certain cases.</li>
            <li><strong>Withdraw Consent:</strong> Stop using optional features or services where consent was given.</li>
          </ul>
          <p>
            To exercise these rights or for support, contact us using the details below. We may require identity verification to process your request.
          </p>
        </section>


        <section>
          <h2>Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes via email, in-app notifications, or by posting the updated policy on our platform with a revised effective date.
          </p>
          <p>
            Please review this Privacy Policy regularly to stay informed about how we handle your data.
          </p>
        </section>


        <section>
          <h2>Contact Us</h2>
          <p>
            For questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
          </p>
          <ul className="contact-list">
            <li><strong>📧 Email:</strong> hello@vaahanbuddy.com</li>
            <li><strong>📞 Phone:</strong> +91 95544 94415</li>
            <li>
              <strong>🏢 Address:</strong> Shop No - 42 First Floor, GDA Commercial Complex, Buddha Vihar Part-A, Taramandal Gorakhpur, U.P. - 273016
            </li>
          </ul>
          <p>
            <strong>🔗 Additional Resources:</strong>
          </p>
          <ul className="contact-list">
            <li><a href="https://www.vaahanbuddy.com/privacy-policy">Privacy Policy</a></li>
            <li><a href="https://www.vaahanbuddy.com/terms-conditions">Terms of Use</a></li>
          </ul>
        </section>
      </div>
    </div>
  );
}


export default PrivacyPolicy;
